/**
 * Rainy season drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawing_topics`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const rainySeasonDrawingsPack: ToilePack = {
    slug: 'rainy-season-drawings',
    category: 'coloring',
    name: 'Rainy season drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'umbrella', label: 'Umbrella', subject: 'a child holding an umbrella in the rain with puddles and a frog'},
        {key: 'boat', label: 'Paper boat', subject: 'a paper boat floating in a stream of rainwater beside a house'},
        {key: 'rainbow', label: 'Rainbow', subject: 'a rainbow over green fields with clouds and rain falling'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil sketch', style: 'a soft graphite pencil sketch with clear outlines and light hatching for shade', palette: 'Gray pencil on white paper, no color.', framing: 'Drawn on a plain white paper sheet, centered with a white margin.'},
        {key: 'crayon', label: 'Colour pencil', style: 'a colour pencil and crayon drawing, simple outlines filled with bright even strokes of colour', palette: 'Bright crayon colours on white paper.', framing: 'Drawn on a plain white drawing-book page, centered with a white margin.'},
        {key: 'outline', label: 'Outline to colour', style: 'a bold black outline drawing with flat marker colour filling each shape', palette: 'Black outlines with flat bright colour fills on white paper.', framing: 'Flat 2D drawing isolated on a pure white background with a white margin, clean closed black line work.'},
    ],
    directions: ['a frog', 'puddles', 'peacock dance'],
    directionHint: 'try: a frog',
    seo: {
        title: 'Rainy Season Drawings: Umbrella, Paper Boat, Rainbow',
        metaDescription: 'Nine rainy season drawings: a child with an umbrella, a paper boat and a rainbow over fields, in pencil, colour and outline.',
        h1: 'Rainy season drawings: child with umbrella, paper boat, rainbow over fields',
        tagline: 'Three monsoon scenes, each in pencil sketch, colour pencil and outline to colour.',
        intro: 'Rainy season drawings for a monsoon chart, a competition or a young child. The set shows a child holding an umbrella in the rain with puddles and a frog, a paper boat floating in a stream of rainwater, and a rainbow over green fields with falling rain. Each comes in three styles: pencil, colour pencil and outline to colour. Type a change in the direction field, such as puddles, a frog or peacock dance.',
        useCases: [
            'a monsoon chart for a class',
            'an easy drawing for a young child',
            'a classroom display for the rainy season',
            'a colouring page for a school project',
        ],
        faq: [
            {q: 'Can I print these for a school competition?', a: 'Yes. Each picture is a plain drawing on a white background, so it prints on ordinary paper.'},
            {q: 'What is the difference between the three styles?', a: 'Pencil sketch is grey shading with no colour. Colour pencil is bright crayon colour. Outline to colour is a bold black line drawing with flat colour inside each shape, easy to fill in.'},
            {q: 'Which picture is easiest for a young child?', a: 'The paper boat has few parts and clear outlines, so it is the simplest of the three scenes.'},
            {q: 'Can I ask for a different picture?', a: 'Yes. Type the change in the direction field. It is a request, so the result may differ from the nine shown.'},
        ],
    },
}
